import localStorage from "local-storage-fallback";
import isPast from "date-fns/isPast";
import addSeconds from "date-fns/addSeconds";
import differenceInSeconds from "date-fns/differenceInSeconds";
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from "react";

export const TimerContext = createContext({
  getTimer: (_key: string) => {},
  setTimer: (_key: string, _countdown: number) => {},
  clearTimer: (_key: string) => {}
});

const TIMER_KEY_LS = "bf-timers-";
const TIMER_EXPIRED_AT_KEY_LS = "bf-timers-expired-at-";
type Timers = Record<string, number>;

window.timeoutList = {};

export default function TimerProvider({ children }: PropsWithChildren<{}>) {
  const [timersValue, setTimersValue] = useState<Timers>({});

  function setTimer(key: string, countdown: number) {
    const countdownKey = TIMER_KEY_LS + key;
    const expiredAtKey = TIMER_EXPIRED_AT_KEY_LS + key;
    const today = new Date();
    const expiredAt = addSeconds(today, countdown);

    localStorage.setItem(countdownKey, `${countdown}`);
    localStorage.setItem(expiredAtKey, expiredAt.toString());

    setTimersValue((prevValues) => ({ ...prevValues, [countdownKey]: countdown }));

    window.timeoutList[countdownKey] = setInterval(() => {
      setTimersValue((prevValues = {}) => {
        const oldValue = +localStorage.getItem(countdownKey) || 0;
        if (oldValue === 0) {
          clearTimer(key);
          delete prevValues[countdownKey];
          return { ...prevValues };
        }
        const newValue = oldValue - 1;
        localStorage.setItem(countdownKey, `${newValue}`);
        return { ...prevValues, [countdownKey]: newValue };
      });
    }, 1000);
  }

  const getTimer = useCallback(
    (key: string) => {
      const localStorageKey = TIMER_KEY_LS + key;
      return timersValue?.[localStorageKey] || 0;
    },
    [timersValue]
  );

  function clearTimer(key: string) {
    const countdownKey = TIMER_KEY_LS + key;
    const expiredAtKey = TIMER_EXPIRED_AT_KEY_LS + key;
    localStorage.removeItem(countdownKey);
    localStorage.removeItem(expiredAtKey);
    clearInterval(window.timeoutList[countdownKey]);
  }

  // init timers
  useEffect(() => {
    Object.entries(localStorage).forEach(([localStorageKey, value]) => {
      // clear the timer is expired date is alread passed
      if (localStorageKey.startsWith(TIMER_EXPIRED_AT_KEY_LS)) {
        const key = localStorageKey.split(TIMER_EXPIRED_AT_KEY_LS)[1];
        const countdownKey = `${TIMER_KEY_LS}${key}`;
        const todayDate = new Date();
        const expiredAtDate = new Date(value);
        if (isPast(expiredAtDate)) {
          clearTimer(key);
        } else {
          const countdownLeft = differenceInSeconds(expiredAtDate, todayDate) + 1;
          localStorage.setItem(countdownKey, String(countdownLeft));
          setTimer(key, countdownLeft);
        }
      }
    });
  }, []);

  useEffect(() => {
    return () => {
      Object.keys(window.timeoutList).forEach((key) => {
        clearInterval(window.timeoutList[key]);
      });
    };
  }, []);

  return (
    <TimerContext.Provider
      value={{
        getTimer,
        setTimer,
        clearTimer
      }}
    >
      {children}
    </TimerContext.Provider>
  );
}

export function useTimer() {
  return useContext(TimerContext);
}
